import React, { useState } from 'react';
import { AlertCircle, CheckCircle2 } from 'lucide-react';

/**
 * ValidatedInput - Input field with real-time validation.
 *
 * Props:
 * - label: string
 * - required: boolean
 * - type: 'text' | 'email' | 'tel' | 'date' | 'password' | 'number'
 * - value, onChange (standard)
 * - validate: function(value) => error message or null
 * - format: function(value) => formatted value (optional)
 * - placeholder: string
 * - maxLength, disabled, etc.
 * - helpText: string (optional tip shown below)
 */
export const ValidatedInput = ({
  label,
  required = false,
  type = 'text',
  value = '',
  onChange,
  validate,
  format,
  placeholder,
  helpText,
  disabled = false,
  maxLength,
  style,
  ...rest
}) => {
  const [touched, setTouched] = useState(false);
  const [focused, setFocused] = useState(false);

  const error = touched && validate ? validate(value) : null;
  const isValid = touched && !error && value && value.length > 0;

  const handleChange = (e) => {
    let newVal = e.target.value;
    if (format) newVal = format(newVal);
    onChange(newVal);
  };

  return (
    <div className="form-group">
      {label && (
        <label className={required ? 'required' : ''}>{label}</label>
      )}
      <div style={{ position: 'relative' }}>
        <input
          type={type}
          value={value}
          onChange={handleChange}
          onBlur={() => setTouched(true)}
          onFocus={() => setFocused(true)}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={maxLength}
          style={{
            paddingRight: (error || isValid) ? 36 : undefined,
            borderColor: error ? '#EF4444' : isValid ? '#10B981' : undefined,
            ...style,
          }}
          {...rest}
        />
        {/* Validation icon */}
        {(error || isValid) && (
          <span style={{
            position: 'absolute', right: 12, top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex', alignItems: 'center',
            pointerEvents: 'none',
          }}>
            {error ? (
              <AlertCircle size={16} color="#EF4444" strokeWidth={2} />
            ) : (
              <CheckCircle2 size={16} color="#10B981" strokeWidth={2} />
            )}
          </span>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div style={{
          fontSize: 12, color: '#EF4444', marginTop: 4,
          display: 'flex', alignItems: 'center', gap: 4,
              }} className="error-txt">
          {error}
        </div>
      )}

      {/* Help text (shown only when no error) */}
      {!error && helpText && (
        <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 4 }}>
          {helpText}
        </div>
      )}
    </div>
  );
};
